import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { AppError } from '../../../../../framework/models/errors';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useGetOutsourceMember } from '../../../../hooks/queries/outsourceMember';
import useToastHook from '../../../../hooks/ToastHook';
import { Worker } from '../../../../models/gen/worker';
import { Formatter } from '../../../../utils/FormatUtils';
import Wireframe from '../../../wireframe/Wireframe';

/**
 * 外注メンバ画面パラメータ
 */
export type OutsourcerWorkerParams = {
  outsourcerMemberId: string;
};

/**
 * 外注メンバ画面
 *
 * - URL `/outsourcer/worker/:outsourcerMemberId/{mode}`
 *   - mode: create / edit / view
 * - パラメータ
 *   - outsourcerMemberId: 職人 ID
 * - クエリ: なし
 *
 * @returns
 */
const OutsourcerWorker: React.FunctionComponent = () => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const params = useParams<OutsourcerWorkerParams>();
  const { data, error, isError, isLoading, refetch } = useGetOutsourceMember({
    id: parseInt(params.outsourcerMemberId!),
  });
  const { toast } = useToastHook();
  const form = useForm<Worker>();
  React.useState(() => {
    if (TypeUtils.isNonNilString(params.outsourcerMemberId)) {
      refetch();
    }
  });
  React.useEffect(() => {
    if (!isLoading) {
      form.reset({
        ...Object.entries(data?.result || {}).reduce(
          (prev, [curK, curV]) => ({
            ...prev,
            [curK]: TypeUtils.toStrictValue(curV),
          }),
          {}
        ),
        dateOfBirth: Formatter.date(data?.result?.dateOfBirth),
        dateFrom: Formatter.date(data?.result?.dateFrom, false),
        dateTo: Formatter.date(data?.result?.dateTo, false, '9999/12/31'),
        hiredDate: Formatter.date(data?.result?.hiredDate),
        firstDateOfPayRaise: Formatter.date(data?.result?.hiredDate),
        workerReference: {
          ...Object.entries(data?.result?.workerReference || {}).reduce(
            (prev, [curK, curV]) => ({
              ...prev,
              [curK]: TypeUtils.toStrictValue(curV),
            }),
            {}
          ),
          startDateOfExperience: Formatter.date(
            data?.result?.workerReference?.startDateOfExperience
          ),
          lastHealthCheckDate: Formatter.date(
            data?.result?.workerReference?.lastHealthCheckDate
          ),
          specialHealthCheckDate: Formatter.date(
            data?.result?.workerReference?.specialHealthCheckDate
          ),
        },
      });
    }
  }, [isLoading, data?.result]);
  React.useEffect(() => {
    if (isError) {
      toast(
        error instanceof AppError ? error : 'メンバ情報の取得に失敗しました'
      );
    }
  }, [isError]);

  return (
    <Wireframe isLoaded={!isLoading} title={pageNames.WORKER}>
      <FormProvider {...form}>
        <Outlet
          context={{
            worker: data?.result,
            isLoading,
            refetch,
            isOutsourceMember: true,
          }}
        />
      </FormProvider>
    </Wireframe>
  );
};

export default OutsourcerWorker;
